import { useRef, useState, useEffect } from "react";
import type SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Keyboard } from "swiper/modules";
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import CancelIcon from '@mui/icons-material/Cancel';
import { Tooltip } from "@mui/material";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../utils/constants";

export default function ImageSliderZoom({ images, selectedImageSlide, isOpen, onClose }) {
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isZoomedIn, setIsZoomedIn] = useState(false);
    const [activeIndex, setActiveIndex] = useState(selectedImageSlide || 0);
    const zoomRefs = useRef<(ReactZoomPanPinchRef | null)[]>([]);
    const mainSwiperRef = useRef<SwiperCore | null>(null);
    const lastScales = useRef<number[]>([]);

    // Initialize zoom refs and scales
    useEffect(() => {
        zoomRefs.current = zoomRefs.current.slice(0, images.length);
        lastScales.current = new Array(images.length).fill(1);
    }, [images.length]);

    useEffect(() => {
        setActiveIndex(selectedImageSlide);
    }, [selectedImageSlide]);

    useEffect(() => {
        if (thumbsSwiper && !thumbsSwiper.destroyed) {
            thumbsSwiper.slideTo(activeIndex);
            if(zoomRefs.current && zoomRefs.current?.length > 0) {
                zoomRefs.current.map(ref => {
                    ref?.resetTransform();
                    ref?.centerView(1, 0);
                })
            }
        }
    }, [activeIndex, thumbsSwiper]);

    const handleZoom = (index: number) => ({ state }) => {
        const currentScale = state.scale;
        const shouldDisableSwipe = currentScale > 1;
        setIsZoomedIn(shouldDisableSwipe);

        // Reset to center if zoomed out to exactly 1
        if (currentScale === 1 && lastScales.current[index] !== 1) {
            requestAnimationFrame(() => {
                if (zoomRefs.current[index]) {
                    zoomRefs.current[index]?.resetTransform();
                    zoomRefs.current[index]?.centerView(1, 0);
                }
            });
        }

        lastScales.current[index] = currentScale;

        if (mainSwiperRef.current) {
            mainSwiperRef.current.allowTouchMove = !shouldDisableSwipe;
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="image-slider-overlay slider-with-zoom">
            <div className="image-slider-container">
                <Swiper
                    modules={[Navigation, Thumbs, Keyboard]}
                    initialSlide={selectedImageSlide}
                    navigation
                    keyboard
                    spaceBetween={10}
                    thumbs={{ swiper: thumbsSwiper }}
                    className="main-swiper"
                    onSwiper={(swiper) => {
                        mainSwiperRef.current = swiper;
                    }}
                    onSlideChange={(swiper) => {
                        const newIndex = swiper.realIndex;
                        setActiveIndex(newIndex);
                        // Reset zoom for all slides when changing
                        zoomRefs.current.forEach(ref => {
                            if (ref) {
                                ref.resetTransform();
                                ref.centerView(1, 0);
                            }
                        });
                        if (thumbsSwiper && !thumbsSwiper.destroyed) {
                            thumbsSwiper.slideTo(newIndex);
                        }
                        setIsZoomedIn(false);
                        if (mainSwiperRef?.current?.allowTouchMove === false) {
                            mainSwiperRef.current.allowTouchMove = true;
                        }
                    }}
                >
                    <span className="close-btn" onClick={() => {
                        setThumbsSwiper(null);
                        onClose();
                    }}>
                        <CancelIcon className="cancel-icon" />
                    </span>
                    {images.map((img, idx) => (
                        <SwiperSlide key={idx}>
                            <TransformWrapper
                                ref={(ref: ReactZoomPanPinchRef) => zoomRefs.current[idx] = ref}
                                initialScale={1}
                                minScale={1}
                                maxScale={5}
                                doubleClick={{
                                    mode: "reset",
                                    step: 0.5
                                }}
                                limitToBounds={true}
                                centerOnInit
                                wheel={{ step: 0.1, smoothStep: 0.005 }}
                                panning={{
                                    disabled: false,
                                    velocityDisabled: true,
                                    allowRightClickPan: false,
                                    allowMiddleClickPan: false,
                                    allowLeftClickPan: true
                                }}
                                onPanningStart={(ref, event) => {
                                    if ((event as any)?.button !== 0) {
                                        event.preventDefault();
                                    } else {
                                        setIsDragging(true);
                                    }
                                }}
                                onPanningStop={() => {
                                    setIsDragging(false);
                                }}
                                onZoom={handleZoom(idx)}
                                onZoomStop={handleZoom(idx)}
                            >
                                {() => (
                                    <TransformComponent
                                        wrapperStyle={{
                                            cursor: isZoomedIn
                                                ? isDragging ? "grabbing" : "grab"
                                                : "default",
                                        }}
                                        wrapperClass="zoom-image-wrapper"
                                    >
                                        <img
                                            className="zoom-slider-image"
                                            src={img.imageUrl}
                                            alt={`Slide ${idx}`}
                                            draggable={false}
                                        />
                                    </TransformComponent>
                                )}
                            </TransformWrapper>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <div className="zoom-slider-tooltip-container">
                    <div className="zoom-slider-tooltip">
                        <Tooltip title={images[activeIndex]?.fileName}>
                            <div className="image-caption">{images[activeIndex]?.imageFileName}</div>
                        </Tooltip>
                        {images[activeIndex]?.attachmentType && <div className="image-type">Attachment type: {images[activeIndex]?.attachmentType}</div>}
                        <div>
                            {images[activeIndex]?.attachmentDate ? moment.utc(images[activeIndex]?.attachmentDate).local().format(DATE_TIME_FORMAT) : ""}
                        </div>
                    </div>
                </div>

                <Swiper
                    onSwiper={setThumbsSwiper}
                    modules={[Thumbs]}
                    spaceBetween={10}
                    watchSlidesProgress
                    className="thumbs-swiper"
                    style={{ marginTop: "10px" }}
                    freeMode={false}
                    grabCursor={true}
                    slidesPerView="auto"
                    centeredSlides={true}
                    slideToClickedSlide={true}
                    initialSlide={selectedImageSlide}
                >
                    {images.map((img, idx) => (
                        <SwiperSlide key={idx} className={idx === activeIndex ? "active-thumb" : ""}>
                            <img src={img.imageUrl} alt={`Thumb ${idx}`} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
}
